import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Search, HelpCircle, Shield, CheckCircle2, ShieldAlert } from "lucide-react";
import Footer from "../components/Footer";

export default function PrivacyPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const sections = useMemo(() => [
    {
      id: "collection",
      title: "1. Information We Collect",
      searchText: "We collect information necessary to deliver personalized nutrition guidance. Account Info, Health Parameters, Health Conditions, Nutrition Logs, Contact & Schedule, Secure Authentication, Payment Processors",
      content: (
        <div className="space-y-4">
          <p>We collect information necessary to deliver personalized nutrition guidance. This includes:</p>
          <ul className="space-y-2 list-none pl-1">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Account Info:</strong> Your name, email, Google user ID, and profile picture (via secure Google sign-in).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Health Parameters:</strong> Age, weight, height, target weight, diet preference (veg/non-veg/vegan), and activity levels.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Health Conditions:</strong> Pre-existing conditions you choose to declare (e.g., diabetes, hypertension).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Nutrition Logs:</strong> Detailed logs of what you eat, macronutrient breakdowns, and daily weight tracking.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Contact & Schedule:</strong> Your phone number for accountability calls and preferred call times.</span>
            </li>
          </ul>
        </div>
      ),
      highlights: ["We collect basic account details and your specific health goals.", "All nutrition logs and weight logs are securely saved to customize your AI recommendations."]
    },
    {
      id: "usage",
      title: "2. How We Use Your Data",
      searchText: "Your data is used to customize and power the Nutri Saathi coaching core: Plan Personalization, meal plans, Accountability Calls, check-in, Chat Interactions, calories, Subscription Control",
      content: (
        <div className="space-y-4">
          <p>Your data is used to customize and power the Nutri Saathi coaching core:</p>
          <ul className="space-y-2 list-none pl-1">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Plan Personalization:</strong> Our AI uses your age, weight, activity levels, and preferences to formulate appropriate meal plans.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Accountability Calls:</strong> Your phone number and schedule preference are used to trigger check-in calls.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Chat Interactions:</strong> AI model API calls process your queries to estimate meal calories and answer questions.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Subscription Control:</strong> Status checks confirm whether your subscription is active, trial, or expired.</span>
            </li>
          </ul>
        </div>
      ),
      highlights: ["We use your details to power personalized meal models.", "Phone number is solely used to deliver accountability calls."]
    },
    {
      id: "security",
      title: "3. Data Security & Storage",
      searchText: "We implement standard safety measures to keep your data secure: Authentication, Google account passwords, Data Storage, database, Payment Security, secure payment gateways, credit card numbers, CVVs, net banking",
      content: (
        <div className="space-y-4">
          <p>We implement standard safety measures to keep your data secure:</p>
          <ul className="space-y-2 list-none pl-1">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Authentication:</strong> Powered by secure identity federation systems. We never see or store your Google account passwords.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Data Storage:</strong> Data is stored in secure cloud database instances with strict connection and access controls.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Payment Security:</strong> Payments are processed end-to-end via secure payment gateway partners. Nutri Saathi does not access, collect, or store your credit card numbers, CVVs, or net banking credentials.</span>
            </li>
          </ul>
        </div>
      ),
      highlights: ["Authentication is handled securely via standard Google Sign-In protocols.", "We do not store or see credit card/payment details."]
    },
    {
      id: "third-party",
      title: "4. Third-Party Data Processing",
      searchText: "We do not sell or trade your personal information. Third-Party Data Processing: AI Models, Telephony Partners, secure calling, Payment Gateways, billing",
      content: (
        <div className="space-y-4">
          <p>We do not sell or trade your personal information. To operate the service, we share specific parameters with trusted third-party providers under confidentiality guidelines:</p>
          <ul className="space-y-2 list-none pl-1">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>AI Models:</strong> Meal descriptions and chat prompts are processed securely using cloud artificial intelligence APIs to generate nutritional estimates.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Telephony Providers:</strong> Your phone number is shared securely with our automated calling infrastructure partners solely to deliver check-in calls.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Payment Processors:</strong> Secure payment gateway partners handle all transaction and billing operations.</span>
            </li>
          </ul>
        </div>
      ),
      highlights: ["We never sell your private information or nutrition logs.", "We use secure partners to process calling, AI estimations, and payments."]
    },
    {
      id: "user-rights",
      title: "5. Your Rights & Data Deletion",
      searchText: "You retain full control over your personal data: Update Profile, Opt-out of Calls, Request Deletion, delete account, support email raghav@nutrisaathi.life",
      content: (
        <div className="space-y-4">
          <p>You retain full control over your personal data. You can:</p>
          <ul className="space-y-2 list-none pl-1">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Update Profile:</strong> Edit your age, target weight, activity levels, or health conditions anytime in the Dashboard profile modal.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Opt-out of Calls:</strong> Pause or opt-out of accountability check-in calls in Dashboard Settings.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1 shrink-0">•</span>
              <span><strong>Request Deletion:</strong> Request complete deletion of your account and related nutrition/call history by emailing support at <a href="mailto:raghav@nutrisaathi.life" className="underline hover:text-primary transition-colors">raghav@nutrisaathi.life</a>.</span>
            </li>
          </ul>
        </div>
      ),
      highlights: ["Update your details anytime in the dashboard.", "Opt out of phone calls with a single click in Settings.", "Delete your account and all logs by emailing support."]
    }
  ], []);

  // Filter sections based on search query
  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return sections;
    const lowerQuery = searchTerm.toLowerCase();
    return sections.filter(
      (sec) =>
        sec.title.toLowerCase().includes(lowerQuery) ||
        sec.searchText.toLowerCase().includes(lowerQuery) ||
        sec.highlights.some((hl) => hl.toLowerCase().includes(lowerQuery))
    );
  }, [sections, searchTerm]);

  const handleScrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-background relative flex flex-col">
      {/* Background pattern */}
      <div className="fixed inset-0 opacity-[0.02] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIgnTUwMSUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIwLjA1Ii8+PC9zdmc+')]"></div>

      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <img src="/logo.svg" alt="Nutri Saathi Logo" className="w-6 h-6 object-contain" />
            </div>
            <span className="font-bold text-foreground">Nutri Saathi Trust Center</span>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-12">
        {/* Page Hero */}
        <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
            <Shield className="w-3.5 h-3.5" />
            Privacy Protection
          </div>
          <h1 className="text-4xl font-extrabold text-foreground font-serif">Privacy Policy</h1>
          <p className="text-muted-foreground">
            We value your trust. Find out how we handle, process, and secure your personal details and nutrition logs.
          </p>
          <p className="text-xs text-muted-foreground">Last updated: May 2026</p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-10 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <input
            type="text"
            placeholder="Search details, firebase storage, deletions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white/60 dark:bg-black/20 border border-border/80 focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents Sidebar */}
          <aside className="hidden lg:block lg:col-span-1 space-y-4 sticky top-24 self-start">
            <div className="p-5 rounded-2xl bg-secondary/20 border border-border/40 space-y-4">
              <h3 className="font-bold text-foreground text-sm tracking-wider uppercase flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-primary" />
                <span>On This Page</span>
              </h3>
              <nav className="flex flex-col gap-2.5">
                {sections.map((sec) => (
                  <button
                    key={sec.id}
                    onClick={() => handleScrollTo(sec.id)}
                    className="text-left text-xs font-medium text-muted-foreground hover:text-primary transition-colors focus:outline-none hover:translate-x-1 duration-150"
                  >
                    {sec.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Policy Contents */}
          <section className="col-span-1 lg:col-span-3 space-y-10">
            {filteredSections.length === 0 ? (
              <div className="text-center py-16 bg-secondary/15 rounded-3xl border border-border/40 space-y-3">
                <ShieldAlert className="w-10 h-10 text-accent mx-auto animate-bounce" />
                <p className="text-foreground font-semibold">No results found for "{searchTerm}"</p>
                <p className="text-sm text-muted-foreground">Try searching for keywords like "Razorpay," "deletion," or "collection."</p>
              </div>
            ) : (
              filteredSections.map((sec) => (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="p-6 md:p-8 rounded-3xl bg-card border border-border/50 shadow-sm space-y-5 fade-in scroll-mt-24 transition-all duration-300 hover:shadow-md"
                >
                  <h2 className="text-2xl font-bold text-foreground font-serif">{sec.title}</h2>
                  <div className="text-muted-foreground text-sm leading-relaxed">
                    {sec.content}
                  </div>

                  {/* Highlights Callout */}
                  <div className="p-4 rounded-2xl bg-secondary/30 dark:bg-secondary/10 border border-border/30 space-y-2.5">
                    <span className="text-xs font-bold text-primary uppercase tracking-wider block">Key Takeaways</span>
                    <ul className="space-y-1.5">
                      {sec.highlights.map((hl, index) => (
                        <li key={index} className="flex items-start gap-2 text-xs text-muted-foreground leading-normal">
                          <CheckCircle2 className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                          <span>{hl}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
